/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
import { clsx } from '@accelint/design-foundation/lib/utils';
import styles from './styles.module.css';
import type { ComponentPropsWithRef } from 'react';

/**
 * DrawerContent - Main content area within a DrawerView.
 *
 * Scrollable container for the view's primary content.
 *
 * @param props - ComponentPropsWithRef<'div'>
 * @param props.className - Optional CSS class name.
 * @returns The rendered DrawerContent component.
 *
 * @example
 * ```tsx
 * <DrawerView id={viewId}>
 *   <DrawerHeader title="Settings" />
 *   <DrawerContent>
 *     <p>Your content here</p>
 *   </DrawerContent>
 * </DrawerView>
 * ```
 */
export function DrawerContent({
  className,
  ...rest
}: ComponentPropsWithRef<'div'>) {
  return <div {...rest} className={clsx(styles.content, className)} />;
}
